// b15 (BK39): guiden "Kom igång", i tre steg. Den startas när en plats skapas, och kan köras igen
// under Inställningar → Kom igång.
//
//   1. "Det här hittade vi": fynden från navet, med katalogens namn och status. En enhet kan tas
//      med eller hoppas över, och en okänd enhet kan få ett namn. Rutan för att dela okända
//      enheter med e-VAtek är av som förval.
//   2. Frågorna, med svaren som platsen har nu ifyllda.
//   3. "Så här blir det": förslaget från molnet, med exakt det som ändras. Ägaren bekräftar med ett
//      tryck, eller går tillbaka och ändrar. Molnet gör bara det som förslaget visade.
//
// b16: i steg 1 GX-enhetens adress och nyckeln för varje Victron-enhet över Bluetooth. Nyckeln
// stannar i webbläsaren tills den förseglas till navet, och bara lådan skickas. Fråga 6 visar och
// ändrar de tysta tiderna.
//
// b17: "Det här hittade vi" har delar: det som Båtkoll (Stugkoll) använder, ibockat från början;
// övrigt i Home Assistant och Homey, hopfällt och inte ibockat; övrigt; och okända enheter, aldrig
// ibockade från början. Samma modell blir en rad som kan fällas ut. Steg 1 har också Homey
// (adressen och nyckeln) och GX-enhetens lösenord, som förseglas till navet när ägaren går vidare.
//
// b18: en rad, eller en enhet i en utfälld rad, kan markeras som "Finns på en annan plats". Den
// räknas inte, är inte ibockad och kopplas inte, och står sist under "På andra platser", hopfällt.
// Enhetens namn i Home Assistant eller Homey, rummet och navet den sitter på står vid enheten.
// Under Ändra i steg 3 väljer ägaren larmets källa (bara en) och enheterna vars effekt kopplas.

import type {
  Fyndval,
  Guideforslag,
  Guidefraga,
  Guidefynd,
  Guidegrupp,
  Guidelage,
  Guidesvar,
  Roll,
} from "@batkoll/gemensam";
import {
  GUIDEGRUPPER,
  GX_ANVANDARE,
  GX_LOSENORD,
  HOMEY_ADRESS,
  HOMEY_NYCKEL,
  NYCKEL_HJALP,
  VICTRON_NYCKEL,
} from "@batkoll/gemensam";
import { useEffect, useState } from "preact/hooks";
import { api, Felsvar, forseglaHemlighet, forseglaNyckel, type Konfig } from "../api.ts";
import { Kort } from "../komponenter.tsx";
import { notislage, slaPaNotiser } from "../notiser.ts";
import { Homeyadressfalt, Homeynyckelfalt, homeylage, homeyvarning } from "./homey.tsx";
import { Tystatider } from "./konto.tsx";
import { Gxfalt, Gxlosenordsfalt, losenordstext, Nyckelfalt, nyckeltext } from "./victron.tsx";

/** Texten i rutan för okända enheter: exakt det som skickas. */
export const DELA_TEXT =
  "Dela okända enheter med e-VAtek. Då skickas bara tillverkaren, modellen, produktnamnet och vilka PGN eller förmågor en okänd enhet har, till e-VAteks lista över enheter som saknas i katalogen. Aldrig platsen, namnen, serienummer eller adresser.";

const STATUSKLASS: Record<string, string> = {
  stöds: "status-stods",
  beta: "status-beta",
  planerad: "status-planerad",
  okänd: "status-okand",
};

function startsvar(l: Guidelage): Guidesvar {
  const s: Record<string, unknown> = {};
  for (const f of l.fragor) {
    if (f.id === "fynd" || f.id === "konton") continue;
    s[f.id] = f.svar;
  }
  // b17: utan ägarens val gäller förvalet för enhetens del.
  s.fynd = l.fynd.map((f) => ({ id: f.id, val: f.val ?? f.forval ?? "med", namn: f.egetNamn }));
  // b18: valen under Ändra förra gången.
  if (l.larmkalla) s.larmkalla = l.larmkalla;
  if (l.effekt) s.effekt = l.effekt;
  s.konton = [];
  s.delaOkanda = l.delaOkanda;
  if (l.tysta) s.tysta = l.tysta;
  return s as Guidesvar;
}

/** b16: det som ägaren skriver in för Victron i steg 1. Nycklarna lämnar inte webbläsaren här. */
interface Victronutkast {
  gx: { adress: string; portal: string };
  nycklar: Record<string, string>;
  /** b17: GX-enhetens användarnamn och lösenord. */
  inloggning: { anvandare: string; losenord: string };
}

function Victronfalt(props: { l: Guidelage; v: Victronutkast; satt: (v: Victronutkast) => void }) {
  const { l, v } = props;
  const vic = l.victron;
  if (!vic) return null;
  const ble = l.fynd.filter((f) => f.nyckel !== null && f.nyckel !== undefined);
  return (
    <section aria-label="Victron" class="formular">
      <h3>Victron</h3>
      <Gxfalt
        adress={v.gx.adress}
        portal={v.gx.portal}
        forslag={vic.forslag}
        satt={(gx) => props.satt({ ...v, gx })}
      />
      {(v.gx.adress.trim() !== "" || vic.gx.adress) && vic.lada && (
        <>
          <p class="liten">{losenordstext(vic.losenord, vic.inloggning)}</p>
          <Gxlosenordsfalt
            anvandare={v.inloggning.anvandare}
            losenord={v.inloggning.losenord}
            satt={(inloggning) => props.satt({ ...v, inloggning })}
          />
        </>
      )}
      {ble.map((f) => (
        <div key={f.id} class="victronenhet">
          <span>
            {f.namn}: {nyckeltext(f.nyckel)}
          </span>
          {f.nyckel !== "finns" && (
            <Nyckelfalt
              namn={f.namn}
              varde={v.nycklar[f.id] ?? ""}
              satt={(n) => props.satt({ ...v, nycklar: { ...v.nycklar, [f.id]: n } })}
            />
          )}
        </div>
      ))}
      {ble.length > 0 && <p class="liten">{NYCKEL_HJALP}</p>}
    </section>
  );
}

/** b17: delarnas rubriker i "Det här hittade vi". */
function grupprubrik(g: Guidegrupp, produktnamn: string, n: number): string {
  const rubrik: Record<Guidegrupp, string> = {
    anvands: `Det ${produktnamn} använder`,
    ovrigt_ha: "Övrigt i Home Assistant",
    ovrigt_homey: "Övrigt i Homey",
    ovrigt: "Övrigt",
    okand: "Okända enheter",
    annan: "På andra platser",
  };
  return `${rubrik[g]} (${n})`;
}

/**
 * b18: delen som en enhet står i nu: "På andra platser" när ägaren har markerat den, annars som
 * i molnet (guidegrupp), också när ägaren just har tagit bort markeringen.
 */
function gruppNu(g: Guidefynd, val: Fyndval): Guidegrupp {
  if (val === "annan") return "annan";
  if (g.grupp !== "annan") return g.grupp ?? "anvands";
  if (g.status === "stöds" || g.status === "beta" || g.kopplad) return "anvands";
  if (g.kalla === "ha") return "ovrigt_ha";
  if (g.kalla === "homey") return "ovrigt_homey";
  return g.status === null ? "okand" : "ovrigt";
}

/** b18: enhetens namn i Home Assistant eller Homey, rummet och navet, till exempel "Entré". */
function igenkanning(g: Guidefynd): string | null {
  // b20-3: inte det som namnet redan säger ("KNX-termostater, rummet Sovrummet").
  const delar = [
    g.enhetsnamn && !g.namn.includes(`, ${g.enhetsnamn} (`) ? g.enhetsnamn : null,
    g.rum && !g.namn.includes(`, rummet ${g.rum} (`) ? `rummet ${g.rum}` : null,
    g.via ? `via ${g.via}` : null,
  ].filter((x): x is string => x !== null);
  return delar.length > 0 ? delar.join(", ") : null;
}

/** b17: förmågorna i ord, för en enhet i Home Assistant eller Homey. */
const FORMAGEORD: Record<string, string> = {
  klimat: "klimat",
  temperatur: "temperatur",
  fukt: "fukt",
  effekt: "effekt",
  energi: "energi",
  las: "lås",
  larm: "larm",
  dorr: "dörr",
  fonster: "fönster",
  vatten: "vatten",
  rok: "rök",
  kamera: "kamera (kommer senare)",
  regn: "regn (kopplas inte)",
  utegivare: "utegivare",
};

type Andra = (ider: string[], d: Partial<{ val: Fyndval; namn: string | null }>) => void;

/** b18: valet för en enhet nu: ägarens, eller förvalet för dess del. */
const valNu = (g: Guidefynd, svar: Guidesvar): Fyndval =>
  svar.fynd?.find((f) => f.id === g.id)?.val ?? g.forval ?? "med";

/** b18: valet när markeringen "Finns på en annan plats" tas bort: förvalet för enhetens del. */
const utanAnnan = (g: Guidefynd): Fyndval => (gruppNu(g, "hoppa") === "anvands" ? "med" : "hoppa");

/**
 * b18: rutan "Finns på en annan plats" för en enhet eller en rad. Den står i enhetens rad, och
 * har inte enhetens namn i sitt namn, så att bocken för enheten har ett eget namn.
 */
function Annanplats(props: { enheter: Guidefynd[]; svar: Guidesvar; andra: Andra }) {
  const { enheter, svar } = props;
  const alla = enheter.every((g) => valNu(g, svar) === "annan");
  return (
    <label class="kryss annan-plats">
      <input
        type="checkbox"
        checked={alla}
        onChange={(e) => {
          const pa = (e.target as HTMLInputElement).checked;
          for (const g of enheter) props.andra([g.id], { val: pa ? "annan" : utanAnnan(g) });
        }}
      />
      <span>Finns på en annan plats</span>
    </label>
  );
}

/** En enhet: bocken, namnet, statusen, och ett namn för en okänd enhet som är med. */
function Fyndenhet(props: { g: Guidefynd; svar: Guidesvar; andra: Andra; etikett?: string }) {
  const { g } = props;
  const v = props.svar.fynd?.find((f) => f.id === g.id);
  const val = valNu(g, props.svar);
  const med = val === "med";
  const kanns = igenkanning(g);
  const etikett = props.etikett ?? g.namn;
  return (
    <li class="fynd">
      <label class="kryss">
        <input
          type="checkbox"
          checked={med}
          disabled={val === "annan"}
          onChange={(e) =>
            props.andra([g.id], { val: (e.target as HTMLInputElement).checked ? "med" : "hoppa" })
          }
        />
        <span>{etikett}</span>
      </label>
      {kanns && <span class="liten"> · {kanns}</span>}
      <span class={`statusmarke ${STATUSKLASS[g.statustext] ?? ""}`}>{g.statustext}</span>
      {g.system && <span class="liten"> · {g.system}</span>}
      {g.formagor && g.formagor.length > 0 && (
        <span class="liten"> · {g.formagor.map((f) => FORMAGEORD[f] ?? f).join(", ")}</span>
      )}
      <Annanplats enheter={[g]} svar={props.svar} andra={props.andra} />
      {!g.kand && med && (
        <label>
          Namn för {g.namn}
          <input
            type="text"
            maxLength={40}
            value={v?.namn ?? ""}
            onInput={(e) => {
              const t = (e.target as HTMLInputElement).value;
              props.andra([g.id], { namn: t.trim() === "" ? null : t });
            }}
          />
        </label>
      )}
    </li>
  );
}

/**
 * b17: en rad med enheter av samma modell, till exempel "14 × Shelly 1 Mini Gen4 (via Home
 * Assistant)". Bocken gäller alla, och raden kan fällas ut till en enhet i taget.
 */
function Fyndrad(props: { enheter: Guidefynd[]; svar: Guidesvar; andra: Andra }) {
  const { enheter, svar } = props;
  const forsta = enheter[0] as Guidefynd;
  if (enheter.length === 1) return <Fyndenhet g={forsta} svar={svar} andra={props.andra} />;
  const valt = (g: Guidefynd) => valNu(g, svar) === "med";
  const alla = enheter.every(valt);
  const nagon = enheter.some(valt);
  return (
    <li class="fynd fyndrad">
      <label class="kryss">
        <input
          type="checkbox"
          checked={alla}
          disabled={enheter.every((g) => valNu(g, svar) === "annan")}
          ref={(e) => {
            if (e) e.indeterminate = nagon && !alla;
          }}
          onChange={(e) =>
            props.andra(
              enheter.map((g) => g.id),
              { val: (e.target as HTMLInputElement).checked ? "med" : "hoppa" },
            )
          }
        />
        <span>
          {enheter.length} × {forsta.namn}
        </span>
      </label>
      <span class={`statusmarke ${STATUSKLASS[forsta.statustext] ?? ""}`}>{forsta.statustext}</span>
      {forsta.system && <span class="liten"> · {forsta.system}</span>}
      <Annanplats enheter={enheter} svar={svar} andra={props.andra} />
      <details class="fyndgrupp">
        <summary>Visa alla {enheter.length}</summary>
        <ul class="fyndlista">
          {enheter.map((g, i) => (
            <Fyndenhet
              key={g.id}
              g={g}
              svar={svar}
              andra={props.andra}
              etikett={`${forsta.namn}, ${i + 1} av ${enheter.length}`}
            />
          ))}
        </ul>
      </details>
    </li>
  );
}

/** b17: en del av "Det här hittade vi", med raderna i den. */
function Fyndgrupp(props: {
  grupp: Guidegrupp;
  fynd: Guidefynd[];
  produktnamn: string;
  svar: Guidesvar;
  andra: Andra;
}) {
  const rader = new Map<string, Guidefynd[]>();
  for (const g of props.fynd) {
    const r = g.rad ?? g.id;
    rader.set(r, [...(rader.get(r) ?? []), g]);
  }
  const lista = (
    <ul class="fyndlista">
      {[...rader].map(([r, enheter]) => (
        <Fyndrad key={r} enheter={enheter} svar={props.svar} andra={props.andra} />
      ))}
    </ul>
  );
  const rubrik = grupprubrik(props.grupp, props.produktnamn, props.fynd.length);
  // Övrigt i Home Assistant och Homey, och det som finns på andra platser, är hopfällt från början.
  if (props.grupp === "annan") {
    return (
      <details class="fyndgrupp" aria-label={rubrik}>
        <summary>
          <h3>{rubrik}</h3>
        </summary>
        <p class="liten">
          Räknas inte, är inte ibockat och kopplas inte. Ta bort markeringen om enheten finns här.
        </p>
        {lista}
      </details>
    );
  }
  if (props.grupp === "ovrigt_ha" || props.grupp === "ovrigt_homey") {
    return (
      <details class="fyndgrupp" aria-label={rubrik}>
        <summary>
          <h3>{rubrik}</h3>
        </summary>
        <p class="liten">Inte ibockat från början. Bocka i det som du vill ha med.</p>
        {lista}
      </details>
    );
  }
  return (
    <section class="fyndgrupp" aria-label={rubrik}>
      <h3>{rubrik}</h3>
      {props.grupp === "okand" && <p class="liten">Okända enheter är inte ibockade från början.</p>}
      {lista}
    </section>
  );
}

function Fyndlista(props: {
  l: Guidelage;
  svar: Guidesvar;
  satt: (s: Guidesvar) => void;
  victron: Victronutkast;
  sattVictron: (v: Victronutkast) => void;
  homey: Homeyutkast;
  sattHomey: (h: Homeyutkast) => void;
}) {
  const { l, svar } = props;
  const fynd = svar.fynd ?? [];
  const andra: Andra = (ider, d) =>
    props.satt({
      ...svar,
      fynd: fynd.map((f) => (ider.includes(f.id) ? { ...f, ...d } : f)),
    });
  return (
    <Kort rubrik="Det här hittade vi" bred>
      <p>{l.sammanfattning}</p>
      {l.ingaMotorer && <p class="meddelande">{l.ingaMotorer}</p>}
      {l.fynd.length === 0 ? (
        <p class="liten">
          Navet har inte hittat något än. Det lyssnar på det som finns på platsen, och listan kommer
          inom några minuter efter att navet har startat.
        </p>
      ) : (
        GUIDEGRUPPER.map((grupp) => {
          const delar = l.fynd.filter((g) => gruppNu(g, valNu(g, svar)) === grupp);
          return delar.length === 0 ? null : (
            <Fyndgrupp
              key={grupp}
              grupp={grupp}
              fynd={delar}
              produktnamn={l.produktnamn}
              svar={svar}
              andra={andra}
            />
          );
        })
      )}
      <p class="liten">
        Statusen: stöds är provad på en riktig enhet (eller läses via Home Assistant), beta är byggd
        efter specifikationen och inspelade data, planerad finns inte än, känd används inte av{" "}
        {l.produktnamn}, och okänd saknas i katalogen. Telefoner, klockor och datorer tas aldrig
        med. En surfplatta eller pekskärm som sitter fast på platsen står som en fast skärm, med
        bara modellen.
      </p>
      <label class="kryss">
        <input
          type="checkbox"
          checked={svar.delaOkanda === true}
          onChange={(e) =>
            props.satt({ ...svar, delaOkanda: (e.target as HTMLInputElement).checked })
          }
        />
        <span>{DELA_TEXT}</span>
      </label>
      <Victronfalt l={l} v={props.victron} satt={props.sattVictron} />
      <Homeyfalt l={l} h={props.homey} satt={props.sattHomey} />
    </Kort>
  );
}

/** b17: det som ägaren skriver in för Homey i steg 1. Nyckeln lämnar inte webbläsaren här. */
interface Homeyutkast {
  adress: string;
  nyckel: string;
}

function Homeyfalt(props: { l: Guidelage; h: Homeyutkast; satt: (h: Homeyutkast) => void }) {
  const hem = props.l.homey;
  if (!hem) return null;
  const varning = homeyvarning(hem);
  return (
    <section aria-label="Homey" class="formular">
      <h3>Homey</h3>
      {varning && (
        <p class="fel" role="alert">
          {varning}
        </p>
      )}
      <Homeyadressfalt
        adress={props.h.adress}
        forslag={hem.forslag}
        satt={(adress) => props.satt({ ...props.h, adress })}
      />
      {homeylage(hem).map((t) => (
        <p key={t} class="liten">
          {t}
        </p>
      ))}
      {(props.h.adress.trim() !== "" || hem.adress) && hem.lada && (
        <Homeynyckelfalt
          varde={props.h.nyckel}
          satt={(nyckel) => props.satt({ ...props.h, nyckel })}
        />
      )}
    </section>
  );
}

function Fraga(props: { f: Guidefraga; svar: Guidesvar; satt: (s: Guidesvar) => void }) {
  const { f, svar } = props;
  const v = (svar as Record<string, unknown>)[f.id];
  const satt = (x: unknown) => props.satt({ ...svar, [f.id]: x });
  const namn = `fraga-${f.id}`;
  if (f.sort === "val") {
    return (
      <fieldset>
        <legend>
          {f.nr}. {f.text}
        </legend>
        {f.alternativ.map((a) => (
          <label key={a.id} class="kryss">
            <input type="radio" name={namn} checked={v === a.id} onChange={() => satt(a.id)} />
            <span>{a.text}</span>
          </label>
        ))}
        {f.hjalp && <p class="liten">{f.hjalp}</p>}
      </fieldset>
    );
  }
  if (f.sort === "flerval" || f.sort === "larm") {
    const valda = new Set(Array.isArray(v) ? (v as string[]) : []);
    return (
      <fieldset>
        <legend>
          {f.nr}. {f.text}
        </legend>
        {f.alternativ.map((a) => {
          const finnsInte = a.id === "epost" || a.id === "kameror";
          return (
            <label key={a.id} class="kryss">
              <input
                type="checkbox"
                disabled={finnsInte}
                checked={valda.has(a.id) && !finnsInte}
                onChange={(e) => {
                  const ny = new Set(valda);
                  if ((e.target as HTMLInputElement).checked) ny.add(a.id);
                  else ny.delete(a.id);
                  satt([...ny]);
                }}
              />
              <span>{a.text}</span>
            </label>
          );
        })}
        {f.sort === "larm" && svar.tysta && (
          <Tystatider varde={svar.tysta} satt={(t) => props.satt({ ...svar, tysta: t })} />
        )}
        {f.hjalp && <p class="liten">{f.hjalp}</p>}
      </fieldset>
    );
  }
  if (f.sort === "ja_nej") {
    return (
      <fieldset>
        <legend>
          {f.nr}. {f.text}
        </legend>
        {[
          [true, "Ja"],
          [false, "Nej"],
        ].map(([x, t]) => (
          <label key={String(x)} class="kryss">
            <input type="radio" name={namn} checked={v === x} onChange={() => satt(x)} />
            <span>{t as string}</span>
          </label>
        ))}
        {f.hjalp && <p class="liten">{f.hjalp}</p>}
      </fieldset>
    );
  }
  if (f.sort === "konton") {
    const konton = svar.konton ?? [];
    const andra = (i: number, d: Partial<{ epost: string; roll: Roll }>) =>
      props.satt({ ...svar, konton: konton.map((k, j) => (j === i ? { ...k, ...d } : k)) });
    return (
      <fieldset>
        <legend>
          {f.nr}. {f.text}
        </legend>
        {konton.map((k, i) => (
          <div key={i} class="kontorad">
            <label>
              E-post
              <input
                type="email"
                value={k.epost}
                onInput={(e) => andra(i, { epost: (e.target as HTMLInputElement).value.trim() })}
              />
            </label>
            <label>
              Roll
              <select
                value={k.roll}
                onChange={(e) => andra(i, { roll: (e.target as HTMLSelectElement).value as Roll })}
              >
                <option value="lasare">Läsare</option>
                <option value="agare">Ägare</option>
              </select>
            </label>
          </div>
        ))}
        <button
          type="button"
          class="knapp-andra"
          onClick={() =>
            props.satt({ ...svar, konton: [...konton, { epost: "", roll: "lasare" }] })
          }
        >
          Bjud in någon
        </button>
        {f.hjalp && <p class="liten">{f.hjalp}</p>}
      </fieldset>
    );
  }
  return null;
}

function Forslagsvy(props: { f: Guideforslag }) {
  const f = props.f;
  return (
    <Kort rubrik="Så här blir det" bred>
      {f.mall && (
        <p>
          <strong>{f.mall.namn}.</strong> {f.mall.text}
        </p>
      )}
      {f.uppstallning && (
        <>
          <h3>Båtens uppställning</h3>
          <p>{f.uppstallning.text}</p>
          {f.uppstallning.grupper.length > 0 && (
            <p class="liten">Grupperna: {f.uppstallning.grupper.join("; ")}.</p>
          )}
        </>
      )}
      {f.kort.length > 0 && (
        <>
          <h3>Korten på Hälsa</h3>
          <ul>
            {f.kort.map((k) => (
              <li key={k}>{k}</li>
            ))}
          </ul>
        </>
      )}
      {f.signaler.length > 0 && (
        <>
          <h3>Signalerna</h3>
          <p>{f.signaler.join(", ")}.</p>
        </>
      )}
      {f.granser.length > 0 && (
        <>
          <h3>Gränserna</h3>
          <ul>
            {f.granser.map((g) => (
              <li key={g.grans}>
                {g.text}
                {g.eget ? ` (platsens eget värde: ${String(g.varde).replace(".", ",")})` : ""}
              </li>
            ))}
          </ul>
          <p class="liten">Gränserna ändras under Inställningar.</p>
        </>
      )}
      {f.larm.length > 0 && (
        <>
          <h3>Larmen</h3>
          <ul>
            {f.larm.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </>
      )}
      <section aria-label="Det här ändras">
        <h3>Det här ändras</h3>
        {f.andringar.length === 0 ? (
          <p>Ingenting ändras.</p>
        ) : (
          <ul>
            {f.andringar.map((a) => (
              <li key={a.falt}>{a.text}</li>
            ))}
          </ul>
        )}
      </section>
    </Kort>
  );
}

/**
 * b18: Ändra i steg 3: larmets källa (bara en åt gången, ett larmsystem före ett kamerasystem), och
 * enheterna vars effekt och energi kopplas (bara elmätarna från början).
 */
function Andrakopplingar(props: {
  f: Guideforslag;
  andra: (s: Partial<Guidesvar>) => void;
  upptagen: boolean;
}) {
  const larm = props.f.larmkallor ?? [];
  const effekt = props.f.effekt ?? [];
  if (larm.length < 2 && effekt.length === 0) return null;
  const KALLA = { larmsystem: "larmsystem", kamera: "kamerasystem" } as const;
  return (
    <Kort rubrik="Ändra" bred>
      <details class="fyndgrupp">
        <summary>
          <h3>Ändra larmet och effekten</h3>
        </summary>
        {larm.length > 1 && (
          <fieldset class="formular">
            <legend>Larmets läge kommer från</legend>
            {larm.map((x) => (
              <label class="kryss" key={x.fynd}>
                <input
                  type="radio"
                  name="larmkalla"
                  checked={x.vald}
                  disabled={props.upptagen}
                  onChange={() => props.andra({ larmkalla: x.fynd })}
                />
                <span>
                  {x.namn}
                  {x.kalla ? ` (${KALLA[x.kalla]})` : ""}
                  {x.iNavet ? ", läses redan av navet" : ""}
                </span>
              </label>
            ))}
            <p class="liten">
              Ett larmsystem går före ett kamerasystem. Bara en källa åt gången. Läser navet redan
              larmet i sina inställningar kopplas ingenting nytt för det.
            </p>
          </fieldset>
        )}
        {effekt.length > 0 && (
          <fieldset class="formular">
            <legend>Effekten och energin</legend>
            {effekt.map((x) => (
              <label class="kryss" key={x.fynd}>
                <input
                  type="checkbox"
                  checked={x.vald}
                  disabled={props.upptagen}
                  onChange={(e) => {
                    const pa = (e.target as HTMLInputElement).checked;
                    props.andra({
                      effekt: effekt
                        .filter((y) => (y.fynd === x.fynd ? pa : y.vald))
                        .map((y) => y.fynd),
                    });
                  }}
                />
                <span>
                  {x.namn}
                  {x.elmatare ? " (elmätare)" : ""}
                </span>
              </label>
            ))}
            <p class="liten">
              Föreslås bara för elmätare. En lampa eller ett relä som du har bockat i under steg 1
              kan kopplas här, eller som förbrukare.
            </p>
          </fieldset>
        )}
      </details>
    </Kort>
  );
}

export function Guidevy(props: { platsId: string; konfig: Konfig | null; klar: () => void }) {
  const [l, setL] = useState<Guidelage | null>(null);
  const [svar, setSvar] = useState<Guidesvar>({});
  const [steg, setSteg] = useState<1 | 2 | 3 | 4>(1);
  const [forslag, setForslag] = useState<Guideforslag | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  const [sparar, setSparar] = useState(false);
  const [besked, setBesked] = useState<string | null>(null);
  const [victron, setVictron] = useState<Victronutkast>({
    gx: { adress: "", portal: "" },
    nycklar: {},
    inloggning: { anvandare: "", losenord: "" },
  });
  const [homey, setHomey] = useState<Homeyutkast>({ adress: "", nyckel: "" });

  useEffect(() => {
    api
      .guiden(props.platsId)
      .then((x) => {
        setL(x);
        setSvar(startsvar(x));
        setVictron({
          gx: { adress: x.victron?.gx.adress ?? "", portal: x.victron?.gx.portal ?? "" },
          nycklar: {},
          inloggning: { anvandare: "", losenord: "" },
        });
        setHomey({ adress: x.homey?.adress ?? "", nyckel: "" });
      })
      .catch((e) => setFel(e instanceof Felsvar ? e.message : "Guiden går inte att hämta nu."));
  }, [props.platsId]);

  if (!l) return <p class="laddar">{fel ?? "Hämtar guiden …"}</p>;
  const lage = l;

  /**
   * b16: svaren med Victron: adressen när den har ändrats, och nycklarna förseglade till navet.
   * Nyckeln i klartext skickas aldrig.
   */
  async function svarMedVictron(bas: Guidesvar = svar): Promise<Guidesvar> {
    const konton = (bas.konton ?? []).filter((k) => k.epost !== "");
    const ut: Guidesvar = { ...bas, konton };
    // b17: Homeys adress och nyckel, förseglad till navet.
    const hem = lage.homey;
    if (hem) {
      const adress = homey.adress.trim() || null;
      if (adress !== null && !HOMEY_ADRESS.test(adress)) {
        throw new Felsvar("Skriv Homeys adress på platsens nät, till exempel 192.168.1.50.", 400);
      }
      if (adress !== hem.adress) ut.homey = { adress };
      const n = homey.nyckel.trim();
      if (n) {
        if (!HOMEY_NYCKEL.test(n)) {
          throw new Felsvar("Nyckeln för Homey har 20–400 tecken utan mellanslag.", 400);
        }
        if (!hem.lada)
          throw new Felsvar("Navet kan inte ta emot nyckeln än: det behöver b17.", 400);
        ut.homeyNyckel = await forseglaHemlighet(hem.lada, "homey", { nyckel: n });
      }
    }
    const vic = lage.victron;
    if (!vic) return ut;
    // b17: GX-enhetens lösenord, förseglat till navet.
    if (victron.inloggning.losenord !== "") {
      const { anvandare, losenord } = victron.inloggning;
      if (!GX_LOSENORD.test(losenord) || !GX_ANVANDARE.test(anvandare.trim())) {
        throw new Felsvar("Lösenordet har 1–128 tecken, och användarnamnet högst 64.", 400);
      }
      if (!vic.lada)
        throw new Felsvar("Navet kan inte ta emot lösenordet än: det behöver b17.", 400);
      ut.gxInloggning = await forseglaHemlighet(vic.lada, "gx", {
        anvandare: anvandare.trim(),
        losenord,
      });
    }
    const adress = victron.gx.adress.trim() || null;
    const portal = victron.gx.portal.trim().toLowerCase() || null;
    if (adress !== vic.gx.adress || (adress !== null && portal !== vic.gx.portal)) {
      ut.gx = { adress, portal };
    }
    const lador: NonNullable<Guidesvar["victronNycklar"]> = [];
    for (const [fynd, nyckel] of Object.entries(victron.nycklar)) {
      if (!nyckel.trim()) continue;
      if (!VICTRON_NYCKEL.test(nyckel.trim())) {
        throw new Felsvar("En nyckel ur VictronConnect har 32 tecken, 0–9 och a–f.", 400);
      }
      const publik = vic.enheter.find((e) => e.fynd === fynd)?.lada ?? vic.lada;
      if (!publik) {
        throw new Felsvar("Navet kan inte ta emot nycklar än: det behöver Bluetooth och b16.", 400);
      }
      lador.push({ fynd, lada: await forseglaNyckel(publik, fynd, nyckel) });
    }
    if (lador.length > 0) ut.victronNycklar = lador;
    return ut;
  }

  async function visaForslag() {
    setFel(null);
    setSparar(true);
    try {
      setForslag(await api.guideForslag(props.platsId, await svarMedVictron()));
      setSteg(3);
    } catch (e) {
      setFel(e instanceof Felsvar ? e.message : "Förslaget går inte att hämta nu.");
    } finally {
      setSparar(false);
    }
  }

  /** b18: ett val under Ändra: förslaget räknas om med det. */
  async function andraKoppling(nytt: Partial<Guidesvar>) {
    const ny = { ...svar, ...nytt };
    setSvar(ny);
    setFel(null);
    setSparar(true);
    try {
      setForslag(await api.guideForslag(props.platsId, await svarMedVictron(ny)));
    } catch (e) {
      setFel(e instanceof Felsvar ? e.message : "Förslaget går inte att hämta nu.");
    } finally {
      setSparar(false);
    }
  }

  async function bekrafta() {
    if (!forslag) return;
    setFel(null);
    setSparar(true);
    try {
      const r = await api.guideBekrafta(props.platsId, await svarMedVictron(), forslag.nyckel);
      const delar = [
        r.antal === 0
          ? "Klart. Ingenting ändrades."
          : `Klart. ${r.antal} ${r.antal === 1 ? "ändring" : "ändringar"}, som står i platsens logg.`,
        ...r.meddelanden,
      ];
      if (svar.larm?.includes("push") && props.konfig?.vapidPublik) {
        const lage = await notislage().catch(() => "kan-inte" as const);
        if (lage === "av") await slaPaNotiser(props.konfig.vapidPublik).catch(() => {});
      }
      setBesked(delar.join(" "));
      setSteg(4);
      props.klar();
    } catch (e) {
      if (e instanceof Felsvar && e.status === 409) {
        setFel(e.message);
        setSteg(2);
      } else setFel(e instanceof Felsvar ? e.message : "Det gick inte att bekräfta nu.");
    } finally {
      setSparar(false);
    }
  }

  const rubrik = `Kom igång med ${l.produktnamn}`;
  return (
    <div class="guide">
      <h2>{rubrik}</h2>
      <p class="liten">Steg {Math.min(steg, 3)} av 3</p>
      {fel && (
        <p class="fel" role="alert">
          {fel}
        </p>
      )}
      {steg === 1 && (
        <div class="rutnat">
          <Fyndlista
            l={l}
            svar={svar}
            satt={setSvar}
            victron={victron}
            sattVictron={setVictron}
            homey={homey}
            sattHomey={setHomey}
          />
          <div class="knapprad">
            <button type="button" class="knapp-huvud" onClick={() => setSteg(2)}>
              Nästa: frågorna
            </button>
          </div>
        </div>
      )}
      {steg === 2 && (
        <Kort rubrik="Frågorna" bred>
          <form
            class="formular"
            onSubmit={(e) => {
              e.preventDefault();
              void visaForslag();
            }}
          >
            {l.fragor
              .filter((f) => f.id !== "fynd")
              .map((f) => (
                <Fraga key={f.id} f={f} svar={svar} satt={setSvar} />
              ))}
            <div class="knapprad">
              <button type="button" class="knapp-andra" onClick={() => setSteg(1)}>
                Tillbaka
              </button>
              <button type="submit" class="knapp-huvud" disabled={sparar}>
                Visa förslaget
              </button>
            </div>
          </form>
        </Kort>
      )}
      {steg === 3 && forslag && (
        <div class="rutnat">
          <Forslagsvy f={forslag} />
          <Andrakopplingar f={forslag} andra={(s) => void andraKoppling(s)} upptagen={sparar} />
          <div class="knapprad">
            <button type="button" class="knapp-andra" onClick={() => setSteg(2)}>
              Ändra
            </button>
            <button
              type="button"
              class="knapp-huvud"
              disabled={sparar}
              onClick={() => void bekrafta()}
            >
              Bekräfta
            </button>
          </div>
        </div>
      )}
      {steg === 4 && (
        <Kort rubrik="Klart" bred>
          <p role="status">{besked}</p>
          <p>
            <a href={`#plats/${props.platsId}/halsa`}>Till Hälsa</a> ·{" "}
            <a href={`#plats/${props.platsId}/installningar`}>Till inställningarna</a>
          </p>
        </Kort>
      )}
    </div>
  );
}
